/* import __COLOCATED_TEMPLATE__ from './panel.hbs'; */
import { HOST_CONTROLS } from 'frontend/constants/settings';
import { SHAREABLE_ENTITIES } from 'frontend/constants';
import { action, computed } from '@ember/object';
import { attributeBindings, classNameBindings, classNames } from '@ember-decorators/component';
import { ensureAppointmentEnding } from 'frontend/utils/dialogs';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import Component from '@ember/component';
import SharePopoverComponent from 'frontend/components/call/controls/share-popover';
import classic from 'ember-classic-decorator';
import styles from './panel.module.scss';

@classNameBindings('controlsShown::fadeout', 'sharedEntity:docked')
@attributeBindings('data-test-call-controls-panel')
@classNames(styles.component)
@classic
export default class TwilioCallControlsPanel extends Component {
  @service('twilio/video-stream') videoStream;
  @service('twilio/room') twilioRoom;
  @service chat;
  @service router;
  @service session;
  @service persistentProperties;
  @service floatingUiElements;
  @service appointmentSettings;
  @service appointment;
  @service audioVideoAdapter;

  disableControls = false;
  'data-test-call-controls-panel' = isEmberTesting();

  @reads('videoStream.isAudioPublished') publishAudio;
  @reads('videoStream.isVideoPublished') publishVideo;
  @reads('floatingUiElements.controlsShown') controlsShown;
  @reads('chat.isChatShown') isChatShown;
  @reads('chat.lastUnseenMessage') lastUnseenMessage;
  @reads('twilioRoom.isSharing') isSharing;
  @reads('twilioRoom.sharedEntity') sharedEntity;
  @reads('twilioRoom.isGuestWaitingState') isGuestWaitingState;
  @reads('twilioRoom.shouldShowRatingPage') shouldShowRatingPage;
  @reads('session.roomModel.featureThClinicianAuth') featureThClinicianAuth;
  @reads('session.roomModel.featureThHostControls') featureThHostControls;
  @reads('session.roomModel.featureThChime') featureThChime;
  @reads('session.isHost') isHost;
  @reads('session.inWaitingRoom') inWaitingRoom;
  @reads('appointmentSettings.sharingEnabled') isSharingEnabled;

  @computed(
    'microphoneButtonOptions',
    'cameraButtonOptions',
    'chatButtonOptions',
    'shareButtonOptions',
    'endCallButtonOptions'
  )
  get controlsList() {
    return [
      this.microphoneButtonOptions,
      this.cameraButtonOptions,
      this.chatButtonOptions,
      this.shareButtonOptions,
      this.endCallButtonOptions,
    ].filter(Boolean);
  }

  @action
  toggleMute() {
    return this.audioVideoAdapter.toggleLocalAudio();
  }

  @action
  toggleVideo() {
    return this.audioVideoAdapter.toggleLocalVideo();
  }

  @action
  toggleScreenSharing() {
    this.videoStream.toggleScreenSharing();
  }

  @action
  toggleWhiteboardSharing() {
    this.featureThChime
      ? this.appointment.toggleWhiteboardSharing()
      : this.twilioRoom.toggleWhiteboardSharing();
  }

  @action
  toggleChat() {
    this.chat.toggleChat();
  }

  @action
  async endCall() {
    let shouldShowRatingPage = this.shouldShowRatingPage;

    if (this.isHost && this.appointmentSettings.confirmEnding) {
      let { value, dismiss } = await ensureAppointmentEnding();
      if (value) {
        this.appointmentSettings.update({ [HOST_CONTROLS.confirmEnding]: false });
      }
      if (dismiss) return;
    }

    if (this.featureThChime) {
      this.appointment.leave();
    } else {
      this.videoStream.disconnect();
    }

    if (shouldShowRatingPage) {
      this.router.transitionTo('appointment.rating');
    } else {
      this.router.transitionTo('appointment');
    }
  }

  @computed('publishAudio', 'disableControls')
  get microphoneButtonOptions() {
    return {
      icon: 'control-mic',
      label: 'Mute mic',
      inactiveIcon: 'mic-off',
      inactiveLabel: 'Unmute',
      onClick: this.toggleMute,
      active: this.publishAudio,
      disabled: this.disableControls,
      ariaLabel: `${this.publishAudio ? 'Mute' : 'Unmute'} microphone`,
    };
  }

  @computed('publishVideo', 'disableControls')
  get cameraButtonOptions() {
    return {
      icon: 'control-video',
      label: 'Stop video',
      inactiveIcon: 'cam-off',
      inactiveLabel: 'Start video',
      onClick: this.toggleVideo,
      active: this.publishVideo,
      disabled: this.disableControls,
      ariaLabel: this.publishVideo ? 'Stop video' : 'Start video',
    };
  }

  @computed('isChatShown', 'inWaitingRoom', 'lastUnseenMessage')
  get chatButtonOptions() {
    if (this.inWaitingRoom) return null;

    return {
      icon: 'chat',
      label: 'Chat',
      inactiveIcon: this.lastUnseenMessage ? 'chat-unread' : 'chat',
      inactiveLabel: 'Chat',
      onClick: this.toggleChat,
      active: this.isChatShown,
      ariaLabel: this.isChatShown ? 'Close chat' : 'Open chat',
    };
  }

  @computed(
    'controlsShown',
    'disableControls',
    'isShareHidden',
    'isSharing',
    'sharedEntity',
    'featureThChime'
  )
  get shareButtonOptions() {
    if (this.isShareHidden) return null;

    return {
      label: 'Share',
      onClick: this.isSharing ? this.toggleSharing : null,
      popoverComponent: this.isSharing || !this.controlsShown ? null : SharePopoverComponent,
      active: !this.isSharing,
      ariaLabel: this.sharedEntity ? `Stop ${this.sharedEntity} sharing` : 'Start sharing',
      disabled: this.disableControls || (!this.isSharing && this.sharedEntity),
      disabledTooltip: !this.disableControls
        ? `Another participant is sharing ${
            this.sharedEntity === SHAREABLE_ENTITIES.screen ? 'their screen' : 'a Whiteboard'
          }.`
        : null,
      icon: 'control-screen-share',
      inactiveIcon: 'control-stop-sharing',
      inactiveLabel: 'Stop sharing',
      variant: 'green',
    };
  }

  @computed('inWaitingRoom', 'featureThClinicianAuth', 'isHost', 'disableControls')
  get endCallButtonOptions() {
    let label = !this.featureThClinicianAuth || this.isHost ? 'End' : 'Leave';
    let inactiveLabel = !this.featureThClinicianAuth || this.isHost ? 'Ending...' : 'Leaving...';

    return {
      icon: 'control-call',
      variant: 'red',
      label,
      inactiveLabel,
      onClick: this.endCall,
      active: true,
      ariaLabel: 'End call',
      disabled: this.disableControls,
    };
  }

  @computed('inWaitingRoom', 'isSharingEnabled', 'isHost', 'featureThHostControls')
  get isShareHidden() {
    return (
      this.inWaitingRoom || (this.featureThHostControls && !this.isSharingEnabled && !this.isHost)
    );
  }

  @action
  toggleSharing() {
    if (this.sharedEntity === SHAREABLE_ENTITIES.screen) {
      this.toggleScreenSharing();
    } else if (this.sharedEntity === SHAREABLE_ENTITIES.whiteboard) {
      this.toggleWhiteboardSharing();
    }
  }
}
